<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useAttrs, watch, type CSSProperties, type PropType } from 'vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  hostClass: { type: [String, Array, Object] as PropType<any>, default: '' },
  stageClass: { type: [String, Array, Object] as PropType<any>, default: '' },
  stageStyle: { type: Object as PropType<CSSProperties>, default: () => ({}) },
  designWidth: { type: Number, default: 0 },
  designHeight: { type: Number, default: 0 },
  scaleMultiplier: { type: Number, default: 1 },
  zoomable: { type: Boolean, default: false },
  zoomMin: { type: Number, default: 0.1 },
  zoomMax: { type: Number, default: 2.5 },
  zoomStep: { type: Number, default: 0.25 },
  fitMode: { type: String as PropType<'resize-stage' | 'transform-stage'>, default: 'resize-stage' },
  backgroundUrl: { type: String, default: '' },
  backgroundAlt: { type: String, default: '预览背景' },
  backgroundClass: { type: [String, Array, Object] as PropType<any>, default: '' },
  backgroundEmptyText: { type: String, default: '' },
  overlayItems: { type: Array as PropType<any[]>, default: () => [] },
})

const emit = defineEmits<{
  stageResize: [{ width: number; height: number }]
}>()

const attrs = useAttrs()
const hostEl = ref<HTMLElement | null>(null)
const scrollEl = ref<HTMLElement | null>(null)
const stageEl = ref<HTMLElement | null>(null)
const stageSize = ref({
  width: Math.max(0, props.designWidth || 0),
  height: Math.max(0, props.designHeight || 0),
})
const scrollBoxSize = ref({
  width: 0,
  height: 0,
})
const zoomScale = ref(1)
const pendingScrollAnchor = ref<{ centerX: number; centerY: number } | null>(null)
const hasInitializedScrollPosition = ref(false)
let resizeObserver: ResizeObserver | null = null
let syncFrameHandle = 0

const hostClasses = computed(() => ['preview-surface', props.hostClass])
const scrollClasses = computed(() => [
  'preview-surface-scroll',
  !props.zoomable ? 'preview-surface-scroll--fixed' : '',
])
const stageClasses = computed(() => ['preview-surface-stage', props.stageClass])
const shouldAutoFit = computed(() => props.designWidth > 0 && props.designHeight > 0)
const zoomBounds = computed(() => {
  const min = Number.isFinite(Number(props.zoomMin)) ? Math.max(0.1, Number(props.zoomMin)) : 0.75
  const max = Number.isFinite(Number(props.zoomMax)) ? Math.max(min, Number(props.zoomMax)) : 2.5
  return { min, max }
})
const resolvedZoomStep = computed(() => {
  const value = Number(props.zoomStep)
  return Number.isFinite(value) && value > 0 ? value : 0.25
})
const fitStageScale = computed(() => {
  if (!shouldAutoFit.value || props.designWidth <= 0 || props.designHeight <= 0) return 1
  return Math.min(stageSize.value.width / props.designWidth, stageSize.value.height / props.designHeight)
})
const effectiveScaleMultiplier = computed(() => {
  const baseValue = props.zoomable ? zoomScale.value : props.scaleMultiplier
  const value = Number(baseValue || 1)
  return Number.isFinite(value) && value > 0 ? value : 1
})
const effectiveStageScale = computed(() => (
  props.zoomable
    ? effectiveScaleMultiplier.value
    : fitStageScale.value * effectiveScaleMultiplier.value
))
const zoomLabel = computed(() => `${Math.round(effectiveScaleMultiplier.value * 100)}%`)
const resolvedStageStyle = computed<CSSProperties>(() => {
  if (!shouldAutoFit.value) return props.stageStyle
  if (props.fitMode === 'transform-stage') {
    return {
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: `${props.designWidth}px`,
      height: `${props.designHeight}px`,
      transform: `translate(-50%, -50%) scale(${effectiveStageScale.value})`,
      transformOrigin: 'center center',
    }
  }
  return {
    width: `${stageSize.value.width}px`,
    height: `${stageSize.value.height}px`,
  }
})
const stageContentStyle = computed<CSSProperties>(() => {
  if (!shouldAutoFit.value || props.fitMode !== 'transform-stage') {
    return {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }
  }
  const viewportWidth = Math.max(1, Math.round(props.designWidth * effectiveStageScale.value))
  const viewportHeight = Math.max(1, Math.round(props.designHeight * effectiveStageScale.value))
  return {
    position: 'relative',
    width: `${Math.max(scrollBoxSize.value.width, viewportWidth)}px`,
    height: `${Math.max(scrollBoxSize.value.height, viewportHeight)}px`,
  }
})
const stageViewportStyle = computed<CSSProperties>(() => {
  if (!shouldAutoFit.value || props.fitMode !== 'transform-stage') return {}
  const viewportWidth = Math.max(1, Math.round(props.designWidth * effectiveStageScale.value))
  const viewportHeight = Math.max(1, Math.round(props.designHeight * effectiveStageScale.value))
  return {
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: `${viewportWidth}px`,
    height: `${viewportHeight}px`,
    transform: 'translate(-50%, -50%)',
  }
})

function resolveFitScaleValue() {
  if (!shouldAutoFit.value || props.designWidth <= 0 || props.designHeight <= 0) return 1
  const hostBox = measureHostContentBox()
  if (hostBox && hostBox.width > 0 && hostBox.height > 0) {
    return Math.min(hostBox.width / props.designWidth, hostBox.height / props.designHeight)
  }
  const width = stageSize.value.width || props.designWidth
  const height = stageSize.value.height || props.designHeight
  return Math.min(width / props.designWidth, height / props.designHeight)
}

function resolveDefaultZoomScale() {
  const requested = Number(props.scaleMultiplier || 1)
  if (requested > 0 && requested !== 1) {
    return clampZoom(requested)
  }
  const fitScale = resolveFitScaleValue()
  return clampZoom(Number.isFinite(fitScale) && fitScale > 0 ? fitScale : 1)
}
function getDisplayedStageSize(width: number, height: number) {
  if (!shouldAutoFit.value) {
    return { width, height }
  }
  if (props.fitMode === 'transform-stage') {
    return {
      width: Math.max(1, Math.round(props.designWidth * effectiveStageScale.value)),
      height: Math.max(1, Math.round(props.designHeight * effectiveStageScale.value)),
    }
  }
  return {
    width: Math.max(1, Math.round(width)),
    height: Math.max(1, Math.round(height)),
  }
}

function measureHostContentBox() {
  const target = scrollEl.value || hostEl.value
  if (!target) return null
  const style = window.getComputedStyle(target)
  const paddingX = parseFloat(style.paddingLeft || '0') + parseFloat(style.paddingRight || '0')
  const paddingY = parseFloat(style.paddingTop || '0') + parseFloat(style.paddingBottom || '0')
  return {
    width: Math.max(0, target.clientWidth - paddingX),
    height: Math.max(0, target.clientHeight - paddingY),
  }
}

function syncScrollBoxSize() {
  const scroll = scrollEl.value
  if (!scroll) return
  const width = Math.max(0, scroll.clientWidth)
  const height = Math.max(0, scroll.clientHeight)
  if (scrollBoxSize.value.width === width && scrollBoxSize.value.height === height) return
  scrollBoxSize.value = { width, height }
}

function captureScrollAnchor() {
  const scroll = scrollEl.value
  if (!scroll) return null
  return {
    centerX: scroll.scrollLeft + (scroll.clientWidth / 2),
    centerY: scroll.scrollTop + (scroll.clientHeight / 2),
  }
}

function rememberScrollAnchor() {
  const anchor = captureScrollAnchor()
  if (!anchor) return
  pendingScrollAnchor.value = anchor
}

function centerScrollViewport() {
  const scroll = scrollEl.value
  if (!scroll) return
  const nextLeft = Math.max(0, Math.round((scroll.scrollWidth - scroll.clientWidth) / 2))
  const nextTop = Math.max(0, Math.round((scroll.scrollHeight - scroll.clientHeight) / 2))
  if (Math.abs(scroll.scrollLeft - nextLeft) > 1) {
    scroll.scrollLeft = nextLeft
  }
  if (Math.abs(scroll.scrollTop - nextTop) > 1) {
    scroll.scrollTop = nextTop
  }
}

function restoreScrollViewportAnchor() {
  const scroll = scrollEl.value
  const anchor = pendingScrollAnchor.value
  pendingScrollAnchor.value = null
  if (!scroll || !anchor) return false
  const maxLeft = Math.max(0, scroll.scrollWidth - scroll.clientWidth)
  const maxTop = Math.max(0, scroll.scrollHeight - scroll.clientHeight)
  const nextLeft = Math.min(maxLeft, Math.max(0, Math.round(anchor.centerX - (scroll.clientWidth / 2))))
  const nextTop = Math.min(maxTop, Math.max(0, Math.round(anchor.centerY - (scroll.clientHeight / 2))))
  scroll.scrollLeft = nextLeft
  scroll.scrollTop = nextTop
  return true
}

function syncStageSize() {
  if (!shouldAutoFit.value) {
    const displayedSize = getDisplayedStageSize(
      Number.parseFloat(String(props.stageStyle.width || 0)) || 0,
      Number.parseFloat(String(props.stageStyle.height || 0)) || 0,
    )
    emit('stageResize', displayedSize)
    return
  }
  const hostBox = measureHostContentBox()
  if (!hostBox || hostBox.width <= 0 || hostBox.height <= 0) return
  const scale = Math.min(hostBox.width / props.designWidth, hostBox.height / props.designHeight)
  const width = Math.max(1, Math.floor(props.designWidth * scale))
  const height = Math.max(1, Math.floor(props.designHeight * scale))
  if (stageSize.value.width === width && stageSize.value.height === height) {
    emit('stageResize', getDisplayedStageSize(width, height))
    return
  }
  stageSize.value = { width, height }
  emit('stageResize', getDisplayedStageSize(width, height))
}

function clampZoom(value: number) {
  return Math.min(zoomBounds.value.max, Math.max(zoomBounds.value.min, value))
}

function syncZoomScaleFromProps() {
  zoomScale.value = resolveDefaultZoomScale()
}

function zoomPreview(delta: number) {
  if (!props.zoomable) return
  rememberScrollAnchor()
  zoomScale.value = clampZoom(Number((zoomScale.value + delta).toFixed(2)))
}

function resetZoomPreview() {
  if (!props.zoomable) return
  rememberScrollAnchor()
  zoomScale.value = resolveDefaultZoomScale()
}

function scheduleSyncStageSize() {
  if (syncFrameHandle) {
    cancelAnimationFrame(syncFrameHandle)
  }
  syncFrameHandle = requestAnimationFrame(() => {
    syncFrameHandle = 0
    syncScrollBoxSize()
    syncStageSize()
    const restored = restoreScrollViewportAnchor()
    if (!restored && !hasInitializedScrollPosition.value) {
      centerScrollViewport()
    }
    hasInitializedScrollPosition.value = true
  })
}

function bindResizeObserver() {
  resizeObserver?.disconnect()
  resizeObserver = null
  const host = hostEl.value
  const scroll = scrollEl.value
  if ((!host && !scroll) || typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver(() => {
    scheduleSyncStageSize()
  })
  if (host) resizeObserver.observe(host)
  if (scroll && scroll !== host) resizeObserver.observe(scroll)
}

function handleItemPointerdown(item: any, event: PointerEvent) {
  if (item?.stopPointerDown) event.stopPropagation()
  if (item?.preventPointerDown) event.preventDefault()
  if (typeof item?.onPointerdown === 'function') {
    item.onPointerdown(event)
  }
}

function handleResizePointerdown(handle: any, event: PointerEvent) {
  if (handle?.stopPointerDown !== false) event.stopPropagation()
  if (handle?.preventPointerDown !== false) event.preventDefault()
  if (typeof handle?.onPointerdown === 'function') {
    handle.onPointerdown(event)
  }
}

watch(
  () => [props.designWidth, props.designHeight, props.scaleMultiplier, props.stageStyle.width, props.stageStyle.height],
  () => {
    if (hasInitializedScrollPosition.value) {
      rememberScrollAnchor()
    }
    if (props.zoomable) {
      syncZoomScaleFromProps()
    }
    if (shouldAutoFit.value) {
      stageSize.value = {
        width: Math.max(1, props.designWidth || 1),
        height: Math.max(1, props.designHeight || 1),
      }
    }
    scheduleSyncStageSize()
  },
)

watch(
  () => [zoomScale.value, props.zoomable, props.zoomMin, props.zoomMax, props.zoomStep],
  () => {
    if (props.zoomable) {
      zoomScale.value = clampZoom(zoomScale.value)
      scheduleSyncStageSize()
    }
  },
)

onMounted(() => {
  bindResizeObserver()
  syncStageSize()
  syncZoomScaleFromProps()
  scheduleSyncStageSize()
  window.setTimeout(() => {
    scheduleSyncStageSize()
  }, 0)
  window.setTimeout(() => {
    scheduleSyncStageSize()
  }, 50)
})

onBeforeUnmount(() => {
  if (syncFrameHandle) {
    cancelAnimationFrame(syncFrameHandle)
    syncFrameHandle = 0
  }
  resizeObserver?.disconnect()
  resizeObserver = null
})

defineExpose({
  hostEl,
  scrollEl,
  stageEl,
  stageSize,
})
</script>

<template>
  <div ref="hostEl" :class="hostClasses" v-bind="attrs">
    <div v-if="zoomable" class="preview-surface-zoom">
      <button type="button" class="preview-surface-zoom__button" @click="zoomPreview(-resolvedZoomStep)">-</button>
      <button type="button" class="preview-surface-zoom__button preview-surface-zoom__button--label" @click="resetZoomPreview">
        {{ zoomLabel }}
      </button>
      <button type="button" class="preview-surface-zoom__button" @click="zoomPreview(resolvedZoomStep)">+</button>
    </div>
    <div ref="scrollEl" :class="scrollClasses">
      <div class="preview-surface-scroll-content" :style="stageContentStyle">
        <div class="preview-surface-stage-viewport" :style="stageViewportStyle">
          <div ref="stageEl" :class="stageClasses" :style="resolvedStageStyle">
            <img
              v-if="backgroundUrl"
              :class="backgroundClass"
              :src="backgroundUrl"
              :alt="backgroundAlt"
              draggable="false"
            />
            <div v-else-if="backgroundEmptyText" :class="backgroundClass">
              {{ backgroundEmptyText }}
            </div>
            <component
              :is="item.tag || 'div'"
              v-for="item in overlayItems"
              :key="item.key"
              :class="[item.class, item.dragging ? item.draggingClass : null]"
              :style="item.style"
              :type="item.tag === 'button' ? 'button' : undefined"
              :aria-label="item.ariaLabel"
              draggable="false"
              @pointerdown="handleItemPointerdown(item, $event)"
            >
              <img
                v-if="item.imageUrl"
                :class="item.imageClass"
                :src="item.imageUrl"
                :alt="item.imageAlt || ''"
                draggable="false"
                @load="item.onLoad?.($event)"
              />
              <span v-else-if="item.emptyText">{{ item.emptyText }}</span>
              <div v-if="item.text" :class="item.textClass">{{ item.text }}</div>
              <component
                :is="item.resizeHandle?.tag || 'button'"
                v-if="item.resizeHandle"
                :class="item.resizeHandle.class"
                :style="item.resizeHandle.style"
                :type="item.resizeHandle?.tag === 'button' ? 'button' : undefined"
                :aria-label="item.resizeHandle.ariaLabel"
                draggable="false"
                @pointerdown="handleResizePointerdown(item.resizeHandle, $event)"
              />
            </component>
            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.preview-surface {
  min-height: 0;
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: #000000;
  touch-action: manipulation;
}

.preview-surface-scroll {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  touch-action: manipulation;
}

.preview-surface-scroll--fixed {
  overflow: hidden;
}

.preview-surface-scroll-content {
  position: relative;
  min-width: 100%;
  min-height: 100%;
}

.preview-surface-zoom {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 4px;
}

.preview-surface-zoom__button {
  min-width: 32px;
  height: 26px;
  padding: 0 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(15, 23, 42, 0.88);
  color: #e5eefb;
  cursor: pointer;
}

.preview-surface-zoom__button--label {
  min-width: 58px;
}

.preview-surface-stage {
  position: relative;
  display: flex;
  flex-shrink: 0;
  min-width: 0;
  min-height: 0;
  background: #000000;
}

.preview-surface-stage-viewport {
  position: relative;
  flex-shrink: 0;
  min-width: 0;
  min-height: 0;
  overflow: visible;
}

.preview-surface-stage :deep([draggable="false"]) {
  -webkit-user-drag: none;
}
</style>
